import type {
	ScheduledVrdhCall,
	UpcomingPatientAppointment,
} from '@/api/types/ProviderApi'
import { closestIndexTo } from 'date-fns'
import { useMemo } from 'react'
import { useFetchUpcomingAppointment } from './useFetchUpcomingAppointment'
import { useFetchUpcomingVrdhCall } from './useFetchUpcomingVrdhCalls'

export enum UpcomingScheduleEventType {
	NONE = 'NONE',
	APPOINTMENT = 'APPOINTMENT',
	VRDH_CALL = 'VRDH_CALL',
}

export type UpcomingScheduledEvent = {
	appointment?: UpcomingPatientAppointment
	vrdhCall?: ScheduledVrdhCall
	eventType: UpcomingScheduleEventType
}

export const useFetchUpcomingScheduledEvent = () => {
	const {
		appointment: upcomingAppointment,
		isLoading: isLoadingUpcomingAppointment,
		hasUpcomingAppointment,
	} = useFetchUpcomingAppointment()
	const {
		vrdhCall: upcomingVrdhCall,
		isLoading: isLoadingUpcomingVrdhCalls,
		hasUpcomingCall,
	} = useFetchUpcomingVrdhCall()

	const hasLoadedData = useMemo(
		() => !isLoadingUpcomingAppointment || !isLoadingUpcomingVrdhCalls,
		[isLoadingUpcomingAppointment, isLoadingUpcomingVrdhCalls]
	)

	const scheduledEvent = useMemo((): UpcomingScheduledEvent => {
		if (!hasLoadedData) {
			return {
				eventType: UpcomingScheduleEventType.NONE,
			}
		}

		const resolveUpcomingScheduledEventType = () => {
			if (!hasUpcomingAppointment && hasUpcomingCall) {
				return UpcomingScheduleEventType.VRDH_CALL
			}

			if (hasUpcomingAppointment && !hasUpcomingCall) {
				return UpcomingScheduleEventType.APPOINTMENT
			}

			if (hasUpcomingAppointment && hasUpcomingCall) {
				const closestToNow = closestIndexTo(Date.now(), [
					upcomingAppointment!.startTimeUtc,
					upcomingVrdhCall!.startTimeUtc,
				])
				return closestToNow === 0
					? UpcomingScheduleEventType.APPOINTMENT
					: UpcomingScheduleEventType.VRDH_CALL
			}

			return UpcomingScheduleEventType.NONE
		}

		const upcomingScheduledEventType = resolveUpcomingScheduledEventType()

		if (upcomingScheduledEventType === UpcomingScheduleEventType.VRDH_CALL) {
			return {
				vrdhCall: upcomingVrdhCall!,
				eventType: UpcomingScheduleEventType.VRDH_CALL,
			}
		}

		if (upcomingScheduledEventType === UpcomingScheduleEventType.APPOINTMENT) {
			return {
				appointment: upcomingAppointment!,
				eventType: UpcomingScheduleEventType.APPOINTMENT,
			}
		}

		return {
			eventType: UpcomingScheduleEventType.NONE,
		}
	}, [
		hasLoadedData,
		hasUpcomingAppointment,
		hasUpcomingCall,
		upcomingAppointment,
		upcomingVrdhCall,
	])

	const hasScheduledAppointment = useMemo(
		() =>
			hasLoadedData && scheduledEvent.eventType === UpcomingScheduleEventType.APPOINTMENT,
		[hasLoadedData, scheduledEvent]
	)

	const hasScheduledVrdhCall = useMemo(
		() =>
			hasLoadedData && scheduledEvent.eventType === UpcomingScheduleEventType.VRDH_CALL,
		[hasLoadedData, scheduledEvent]
	)

	const hasNoScheduledEvents = useMemo(
		() => hasLoadedData && scheduledEvent.eventType === UpcomingScheduleEventType.NONE,
		[hasLoadedData, scheduledEvent]
	)

	return {
		isLoading: !hasLoadedData,
		scheduledEvent,
		hasScheduledAppointment,
		hasScheduledVrdhCall,
		hasNoScheduledEvents,
	}
}
